<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSectorPermissions } from 'dashboard/composables/useSectorPermissions';
import ReportHeader from './components/ReportHeader.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import FilterSelector from './components/FilterSelector.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import DropdownContainer from 'dashboard/components-next/dropdown-menu/base/DropdownContainer.vue';
import DropdownBody from 'dashboard/components-next/dropdown-menu/base/DropdownBody.vue';
import DropdownItem from 'dashboard/components-next/dropdown-menu/base/DropdownItem.vue';

const store = useStore();
const { t } = useI18n();
const { isAccountAdmin } = useSectorPermissions();

const dateRange = ref({ since: null, until: null });
const selectedSector = ref(null);

const isLoading = computed(() => store.getters['getWhatsappPricingLoadingStatus']);
const reportData = computed(() => store.getters['getWhatsappPricingReport']);

const allSectors = computed(() => store.getters['sectors/getSectors']);

const userSectorIds = computed(() => {
  const account = store.getters['getCurrentAccount'];
  const memberships = account?.sector_memberships || [];
  return memberships
    .filter(m => m.role === 'sector_admin')
    .map(m => m.sector_id);
});

const sectorOptions = computed(() => {
  if (isAccountAdmin.value) {
    return [
      { id: null, name: t('WHATSAPP_PRICING_REPORTS.FILTER.ALL_SECTORS') },
      ...allSectors.value,
    ];
  }
  return allSectors.value.filter(s => userSectorIds.value.includes(s.id));
});

watch(
  sectorOptions,
  options => {
    if (!isAccountAdmin.value && options.length > 0 && !selectedSector.value) {
      selectedSector.value = options[0];
      fetchData();
    }
  },
  { immediate: true }
);

const currency = computed(() => reportData.value?.currency || 'USD');

const formattedCost = computed(() => {
  const cost = reportData.value?.total_cost ?? 0;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency.value }).format(cost);
});

const chartCollection = computed(() => {
  const { since, until } = dateRange.value;
  const breakdown = reportData.value?.breakdown ?? [];

  const dateMap = {};
  breakdown.forEach(r => { dateMap[String(r.date)] = r; });

  const labels = [];
  const sentData = [];
  const paidData = [];
  const freeData = [];

  if (since && until) {
    const start = new Date(since * 1000);
    const end = new Date(until * 1000);
    start.setUTCHours(0, 0, 0, 0);
    end.setUTCHours(23, 59, 59, 999);
    const cursor = new Date(start);
    while (cursor <= end) {
      const dateStr = cursor.toISOString().split('T')[0];
      const row = dateMap[dateStr];
      labels.push(dateStr);
      sentData.push(row?.sent ?? 0);
      paidData.push(row?.paid ?? 0);
      freeData.push(row?.free ?? 0);
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
  }

  return {
    labels,
    datasets: [
      { label: t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_SENT'), data: sentData, backgroundColor: '#1F93FF' },
      { label: t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_PAID'), data: paidData, backgroundColor: '#FF7E4F' },
      { label: t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_FREE'), data: freeData, backgroundColor: '#44CE4B' },
    ],
  };
});

function exportParams() {
  return {
    since: dateRange.value.since,
    until: dateRange.value.until,
    sectorId: selectedSector.value?.id ?? null,
  };
}

function fetchData() {
  const { since, until } = dateRange.value;
  if (!since || !until) return;
  if (!isAccountAdmin.value && !selectedSector.value) return;
  store.dispatch('fetchWhatsappPricingReport', exportParams());
}

function onDateFilterChange({ from, to }) {
  dateRange.value = { since: from, until: to };
  fetchData();
}

function onSectorChange() {
  fetchData();
}

function downloadCsv() {
  if (!dateRange.value.since || !dateRange.value.until) return;
  store.dispatch('downloadWhatsappPricingCsv', exportParams());
}

function downloadXlsx() {
  if (!dateRange.value.since || !dateRange.value.until) return;
  store.dispatch('downloadWhatsappPricingXlsx', exportParams());
}
</script>

<template>
  <ReportHeader :header-title="t('WHATSAPP_PRICING_REPORTS.HEADER')">
    <DropdownContainer>
      <template #trigger="{ toggle }">
        <V4Button
          :label="t('WHATSAPP_PRICING_REPORTS.EXPORT')"
          icon="i-ph-download-simple"
          size="sm"
          :disabled="!dateRange.since || !dateRange.until"
          @click="toggle"
        />
      </template>
      <DropdownBody>
        <DropdownItem
          :label="t('WHATSAPP_PRICING_REPORTS.DOWNLOAD')"
          icon="i-ph-file-csv"
          :click="downloadCsv"
        />
        <DropdownItem
          :label="t('WHATSAPP_PRICING_REPORTS.DOWNLOAD_XLSX')"
          icon="i-ph-microsoft-excel-logo"
          :click="downloadXlsx"
        />
      </DropdownBody>
    </DropdownContainer>
  </ReportHeader>

  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap gap-2 items-start">
      <FilterSelector
        :show-business-hours-switch="false"
        :show-group-by-filter="false"
        :show-agents-filter="false"
        :show-inbox-filter="false"
        :show-team-filter="false"
        :show-labels-filter="false"
        @filter-change="onDateFilterChange"
      />
      <div class="multiselect-wrap--small">
        <multiselect
          v-model="selectedSector"
          :placeholder="t('WHATSAPP_PRICING_REPORTS.FILTER.SECTOR')"
          label="name"
          track-by="id"
          :options="sectorOptions"
          :show-labels="false"
          :allow-empty="false"
          :disabled="!isAccountAdmin"
          @update:model-value="onSectorChange"
        />
      </div>
    </div>

    <template v-if="dateRange.since">
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
      >
        <ReportMetricCard
          :label="t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_SENT')"
          :info-text="t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_SENT_INFO')"
          :value="String(reportData?.total_sent ?? 0)"
        />
        <ReportMetricCard
          :label="t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_PAID')"
          :info-text="t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_PAID_INFO')"
          :value="String(reportData?.total_paid ?? 0)"
        />
        <ReportMetricCard
          :label="t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_FREE')"
          :info-text="t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_FREE_INFO')"
          :value="String(reportData?.total_free ?? 0)"
        />
        <ReportMetricCard
          :label="t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_COST')"
          :info-text="t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_COST_INFO')"
          :value="formattedCost"
        />
      </div>

      <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5">
        <div class="flex items-center justify-center h-72">
          <span v-if="isLoading" class="text-sm text-n-slate-10">
            {{ t('REPORT.LOADING_CHART') }}
          </span>
          <BarChart v-else :collection="chartCollection" />
        </div>
      </div>

      <div
        v-if="!isLoading && reportData?.breakdown?.length"
        class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 overflow-x-auto"
      >
        <table class="w-full text-sm">
          <thead class="border-b border-n-container">
            <tr>
              <th class="px-6 py-3 text-left text-sm font-medium text-n-slate-11">
                {{ t('WHATSAPP_PRICING_REPORTS.TABLE.DATE') }}
              </th>
              <th class="px-6 py-3 text-right text-sm font-medium text-n-slate-11">
                {{ t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_SENT') }}
              </th>
              <th class="px-6 py-3 text-right text-sm font-medium text-n-slate-11">
                {{ t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_PAID') }}
              </th>
              <th class="px-6 py-3 text-right text-sm font-medium text-n-slate-11">
                {{ t('WHATSAPP_PRICING_REPORTS.METRICS.TOTAL_FREE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in reportData.breakdown"
              :key="row.date"
              class="border-b border-n-container last:border-0"
            >
              <td class="px-6 py-3 text-n-slate-12">{{ row.date }}</td>
              <td class="px-6 py-3 text-right text-n-slate-12">{{ row.sent }}</td>
              <td class="px-6 py-3 text-right text-n-slate-12">{{ row.paid }}</td>
              <td class="px-6 py-3 text-right text-n-slate-12">{{ row.free }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
